import { computeInvoiceTotals } from './zovcurInvoiceStore';

export const normalizeSalesmanName = (name) => String(name || '').trim().toLowerCase();

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const QUARTER_LABELS = {
  1: 'Jan – Mar',
  2: 'Apr – Jun',
  3: 'Jul – Sep',
  4: 'Oct – Dec'
};

export const buildPeriodKey = (periodType, year, month, quarter) => {
  if (periodType === 'monthly') return `${year}-${String(month).padStart(2, '0')}`;
  if (periodType === 'quarterly') return `${year}-Q${quarter}`;
  return String(year);
};

export const getInvoiceBillDate = (invoice) => {
  const raw = invoice?.invoiceDetails?.billDate || invoice?.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const invoiceMatchesPeriod = (invoice, periodType, year, month, quarter) => {
  const date = getInvoiceBillDate(invoice);
  if (!date) return false;

  if (date.getFullYear() !== Number(year)) return false;

  if (periodType === 'monthly') {
    return date.getMonth() + 1 === Number(month);
  }

  if (periodType === 'quarterly') {
    const invoiceQuarter = Math.floor(date.getMonth() / 3) + 1;
    return invoiceQuarter === Number(quarter);
  }

  return true;
};

export const getInvoiceSalesStats = (invoice) => {
  const totals = computeInvoiceTotals(invoice?.products || []);
  return {
    strips: totals.quantity,
    freeStrips: totals.freeQuantity,
    amount: totals.roundedNetAmount,
    invoices: 1
  };
};

export const aggregateInvoicesForSalesman = (invoices, salesmanName, periodType, year, month, quarter) => {
  const normalized = normalizeSalesmanName(salesmanName);

  return invoices.reduce(
    (acc, invoice) => {
      const invSalesman = normalizeSalesmanName(invoice?.invoiceDetails?.salesmanName);
      if (invSalesman !== normalized) return acc;
      if (!invoiceMatchesPeriod(invoice, periodType, year, month, quarter)) return acc;

      const stats = getInvoiceSalesStats(invoice);
      acc.strips += stats.strips;
      acc.freeStrips += stats.freeStrips;
      acc.amount += stats.amount;
      acc.invoices += 1;
      return acc;
    },
    { strips: 0, freeStrips: 0, amount: 0, invoices: 0 }
  );
};

export const findTargetForSalesman = (targets, salesmanId, periodType, year, month, quarter) => {
  const periodKey = buildPeriodKey(periodType, year, month, quarter);
  return targets.find(
    (target) =>
      String(target.salesmanId) === String(salesmanId) &&
      target.periodType === periodType &&
      target.periodKey === periodKey
  );
};

export const calcReachPercent = (achieved, target) => {
  if (!target) return 0;
  return Math.round((achieved / target) * 1000) / 10;
};

/** Remaining amount to reach price target (0 if target met or exceeded). */
export const calcBalancePrice = (target, achieved) => {
  if (!target) return null;
  return Math.max(0, target - achieved);
};

export const formatPeriodLabel = (periodType, year, month, quarter) => {
  if (periodType === 'monthly') {
    return `${MONTH_NAMES[(month || 1) - 1]} ${year}`;
  }
  if (periodType === 'quarterly') {
    return `Q${quarter} (${QUARTER_LABELS[quarter]}) ${year}`;
  }
  return `${year}`;
};

export const buildMonthlyBreakdown = (invoices, salesmanName, year, targets = []) => {
  return MONTH_NAMES.map((label, index) => {
    const month = index + 1;
    const achieved = aggregateInvoicesForSalesman(invoices, salesmanName, 'monthly', year, month);

    const matchedTarget = targets.find(
      (t) =>
        normalizeSalesmanName(t.salesmanName) === normalizeSalesmanName(salesmanName) &&
        t.periodType === 'monthly' &&
        t.periodKey === buildPeriodKey('monthly', year, month)
    );

    const stripTarget = matchedTarget?.stripTarget || 0;
    const amountTarget = matchedTarget?.amountTarget || 0;

    return {
      month,
      label,
      ...achieved,
      stripTarget,
      amountTarget,
      stripReach: calcReachPercent(achieved.strips, stripTarget),
      amountReach: calcReachPercent(achieved.amount, amountTarget),
      amountBalance: calcBalancePrice(amountTarget, achieved.amount)
    };
  });
};

export const buildSalesmanPerformanceRows = ({
  salesmen,
  invoices,
  targets,
  periodType,
  year,
  month,
  quarter
}) => {
  const invoiceSalesmen = new Map();

  invoices.forEach((invoice) => {
    const name = String(invoice?.invoiceDetails?.salesmanName || '').trim();
    if (!name) return;
    const key = normalizeSalesmanName(name);
    if (!invoiceSalesmen.has(key)) {
      invoiceSalesmen.set(key, name);
    }
  });

  const rows = salesmen.map((salesman) => {
    const achieved = aggregateInvoicesForSalesman(
      invoices,
      salesman.name,
      periodType,
      year,
      month,
      quarter
    );
    const target = findTargetForSalesman(targets, salesman._id, periodType, year, month, quarter);

    return {
      id: salesman._id,
      empNo: salesman.empNo,
      name: salesman.name,
      role: salesman.role || 'Salesman',
      stripsAchieved: achieved.strips,
      freeStrips: achieved.freeStrips,
      amountAchieved: achieved.amount,
      invoices: achieved.invoices,
      stripTarget: target?.stripTarget || 0,
      amountTarget: target?.amountTarget || 0,
      stripReach: calcReachPercent(achieved.strips, target?.stripTarget || 0),
      amountReach: calcReachPercent(achieved.amount, target?.amountTarget || 0),
      amountBalance: calcBalancePrice(target?.amountTarget || 0, achieved.amount)
    };
  });

  invoiceSalesmen.forEach((displayName, key) => {
    const alreadyListed = rows.some((row) => normalizeSalesmanName(row.name) === key);
    if (alreadyListed) return;

    const achieved = aggregateInvoicesForSalesman(
      invoices,
      displayName,
      periodType,
      year,
      month,
      quarter
    );

    rows.push({
      id: `invoice-only-${key}`,
      empNo: '—',
      name: displayName,
      role: 'From invoices',
      stripsAchieved: achieved.strips,
      freeStrips: achieved.freeStrips,
      amountAchieved: achieved.amount,
      invoices: achieved.invoices,
      stripTarget: 0,
      amountTarget: 0,
      stripReach: 0,
      amountReach: 0,
      amountBalance: null
    });
  });

  return rows.sort((a, b) => b.amountAchieved - a.amountAchieved);
};

export const getReachTone = (percent) => {
  if (percent >= 100) return 'text-emerald-700 bg-emerald-50';
  if (percent >= 75) return 'text-blue-700 bg-blue-50';
  if (percent >= 50) return 'text-amber-700 bg-amber-50';
  return 'text-rose-700 bg-rose-50';
};
